import { create } from 'zustand';
import { EvidenceInclusion, GroupByOption, ActiveFilter, IdeaWithEvidences, Evidence } from '../types';

// Data structures for ideas and aggregations
interface GroupItemsData {
  items: IdeaWithEvidences[];
  totalCount: number;
  hasNextPage: boolean;
  nextCursor?: string;
}

interface AggregationData {
  impact: number;
  customerCount: number;
  fetchedAt: number; // timestamp for cache invalidation
}

interface GroupAggregateData {
  groupValue: string;
  groupLabel: string;
  totalIdeas: number;
  totalCustomers: number;
  totalImpact: number;
  statusAggregates: Array<{
    statusLabel: string;
    ideasCount: number;
    customersCount: number;
    totalImpact: number;
  }>;
  fetchedAt: number;
}

interface Customer360State {
  // Customer Selection - Unified activeFilters array
  activeFilters: ActiveFilter[]; // Contains both manual selection (relatedRequests.orgId) and filter-based selection
  excludedFiltersBackup: ActiveFilter[]; // transient backup for tab-based exclusion (not persisted)
  totalCustomersCount: number; // Total count from customer selection (modal)
  // Temp selection within modal - full customer objects
  customersArr: any[];
  // Unified selection mode
  selectionMode: 'single' | 'multiple' | 'segment';
  setSelectionMode: (mode: 'single' | 'multiple' | 'segment') => void;
  
  // View Settings
  search: string;
  groupBy: GroupByOption;
  evidenceInclusion: EvidenceInclusion;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  
  // UI State
  expandedGroups: string[];
  selectedIdea: IdeaWithEvidences | null;
  drawerOpen: boolean;
  modalOpen: boolean;
  debugMode: boolean; // Debug mode for development only
  selectedEvidence: Evidence | null;
  
  // 🔥 NEW: Ideas Management (indexed by groupValue)
  ideasByGroup: Record<string, GroupItemsData>;
  
  // 🔥 NEW: Aggregations Cache (indexed by ideaId)
  aggregationsCache: Record<string, AggregationData>;
  
  // 🔥 NEW: Loading States
  loadingGroups: Set<string>; // which groups are loading items
  loadingAggregations: Set<string>; // which idea IDs are loading aggregations
  
  // 🔥 NEW: Group Aggregates Cache (indexed by groupValue)
  groupAggregatesCache: Record<string, GroupAggregateData>;
  loadingGroupAggregates: Set<string>; // which groups are loading aggregates
  
  // Actions
  setSearch: (value: string) => void;
  setActiveFilters: (filters: ActiveFilter[]) => void;
  setTotalCustomersCount: (count: number) => void;
  setGroupBy: (groupBy: GroupByOption) => void;
  setEvidenceInclusion: (inclusion: EvidenceInclusion) => void;
  setSort: (sortBy: string, direction: 'asc' | 'desc') => void;
  toggleGroup: (groupValue: string) => void;
  setExpandedGroups: (groups: string[]) => void;
  selectIdea: (idea: IdeaWithEvidences | null) => void;
  setSelectedEvidence: (evidence: Evidence | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  openModal: () => void;
  closeModal: () => void;
  setDebugMode: (enabled: boolean) => void;
  reset: () => void;
  
  // Helper functions
  getCustomerIdsFromFilters: () => string[];
  hasCustomerSelection: () => boolean;
  setCustomersArr: (customers: any[]) => void;
  clearCustomersArr: () => void;
  toggleCustomerInArr: (customer: any) => void;
  addCustomerIdsFilter: (ids: string[]) => void;
  removeCustomerIdsFilter: () => void;
  addFilter: (filter: ActiveFilter) => void;
  removeFilter: (dbFieldPath: string) => void;

  // Tab-based filter exclusion/restoration (ephemeral)
  excludeFiltersForRoadmap: (dbFieldPathSubstrings: string[]) => void;
  restoreExcludedFiltersForEvidenceTable: () => void;
  
  // 🔥 NEW: Ideas Actions
  setGroupItems: (groupValue: string, items: IdeaWithEvidences[], totalCount: number, hasNextPage: boolean, nextCursor?: string) => void;
  appendGroupItems: (groupValue: string, items: IdeaWithEvidences[], hasNextPage: boolean, nextCursor?: string) => void;
  setGroupLoading: (groupValue: string, isLoading: boolean) => void;
  getGroupItems: (groupValue: string) => IdeaWithEvidences[];
  isGroupLoading: (groupValue: string) => boolean;
  clearGroupItems: (groupValue: string) => void;
  
  // 🔥 NEW: Aggregations Actions
  setAggregations: (aggregations: Record<string, { impact: number; customerCount: number }>) => void;
  setAggregationsLoading: (ideaIds: string[], isLoading: boolean) => void;
  getAggregation: (ideaId: string) => { impact: number; customerCount: number } | undefined;
  isAggregationLoading: (ideaId: string) => boolean;
  clearAggregationsCache: () => void;
  
  // 🔥 NEW: Group Aggregates Actions
  setGroupAggregate: (groupValue: string, data: GroupAggregateData) => void;
  setGroupAggregates: (aggregates: Record<string, GroupAggregateData>) => void;
  setGroupAggregatesLoading: (groupValues: string[], isLoading: boolean) => void;
  getGroupAggregate: (groupValue: string) => GroupAggregateData | undefined;
  isGroupAggregateLoading: (groupValue: string) => boolean;
  clearGroupAggregatesCache: () => void;
}

// Load from localStorage on init
const loadFromStorage = (): Partial<Customer360State> => {
  try {
    const stored = localStorage.getItem('customer360-v2-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Migrate old format to new format if needed
      let activeFilters: ActiveFilter[] = parsed.activeFilters || [];
      if (!activeFilters.length && (parsed.customerIds || parsed.customerFilters)) {
        // Migrate: convert old customerIds/customerFilters to activeFilters
        if (parsed.customerIds && parsed.customerIds.length > 0) {
          activeFilters = [{
            dbFieldPath: 'relatedRequests.orgId',
            selected: parsed.customerIds,
            filterObject: 'evidence',
            selectedIsObjId: true,
            name: 'company',
            label: 'Company Name',
            type: 'fetchableCheckbox',
            showInInbox: true,
            parent: '',
            query: null,
            values: parsed.customerIds
          }];
        } else if (parsed.customerFilters) {
          activeFilters = parsed.customerFilters;
        }
      }
      return {
        activeFilters,
        totalCustomersCount: parsed.totalCustomersCount || 0,
        customersArr: parsed.customersArr || [],
        search: parsed.search || '',
        groupBy: parsed.groupBy || 'domainId',
        evidenceInclusion: parsed.evidenceInclusion || 'total',
        sortBy: parsed.sortBy || 'status',
        sortDirection: parsed.sortDirection || 'asc',
        selectionMode: parsed.selectionMode || 'multiple',
      };
    }
  } catch (e) {
    console.warn('Failed to load customer360-v2 state from localStorage', e);
  }
  return {};
};

// Save to localStorage
const saveToStorage = (state: Partial<Customer360State>) => {
  try {
    localStorage.setItem('customer360-v2-storage', JSON.stringify({
      activeFilters: state.activeFilters,
      totalCustomersCount: state.totalCustomersCount,
      customersArr: state.customersArr,
      groupBy: state.groupBy,
      evidenceInclusion: state.evidenceInclusion,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
      selectionMode: state.selectionMode,
    }));
  } catch (e) {
    console.warn('Failed to save customer360-v2 state to localStorage', e);
  }
};

const initialState = {
  activeFilters: [],
  excludedFiltersBackup: [],
  totalCustomersCount: 0,
  customersArr: [],
  selectionMode: 'multiple' as const,
  search: '',
  groupBy: 'domainId' as GroupByOption,
  evidenceInclusion: 'total' as EvidenceInclusion,
  sortBy: 'status',
  sortDirection: 'asc' as const,
  expandedGroups: [],
  selectedIdea: null,
  selectedEvidence: null,
  drawerOpen: false,
  modalOpen: false,
  debugMode: false,
  // 🔥 NEW: Ideas and Aggregations
  ideasByGroup: {},
  aggregationsCache: {},
  loadingGroups: new Set<string>(),
  loadingAggregations: new Set<string>(),
  // 🔥 NEW: Group Aggregates
  groupAggregatesCache: {},
  loadingGroupAggregates: new Set<string>(),
  ...loadFromStorage(),
};

export const useCustomer360Store = create<Customer360State>()((set, get) => ({
  ...initialState,
  
  setSelectionMode: (mode) => {
    set({ selectionMode: mode });
    saveToStorage({ ...get(), selectionMode: mode });
  },
  
  setSearch: (value) => {
    set({ search: value });
    saveToStorage({ ...get(), search: value });
  },
  
  setActiveFilters: (filters) => {
    set({ activeFilters: filters });
    saveToStorage({ ...get(), activeFilters: filters });
  },
  
  setTotalCustomersCount: (count) => {
    set({ totalCustomersCount: count });
    saveToStorage({ ...get(), totalCustomersCount: count });
  },

  // Manage temp selected customers (full objects) during modal session
  setCustomersArr: (customers) => {
    set({ customersArr: customers });
    saveToStorage({ ...get(), customersArr: customers });
  },
  clearCustomersArr: () => {
    set({ customersArr: [] });
    saveToStorage({ ...get(), customersArr: [] });
  },
  toggleCustomerInArr: (customer) => {
    let updatedCustomersArr: any[] = [];
    set((state) => {
      const id = customer?._id || customer?.id;
      if (!id) {
        updatedCustomersArr = state.customersArr;
        return { customersArr: state.customersArr };
      }
      const exists = state.customersArr.some((c: any) => (c?._id || c?.id) === id);
      updatedCustomersArr = exists
        ? state.customersArr.filter((c: any) => (c?._id || c?.id) !== id)
        : [...state.customersArr, customer];
      return { customersArr: updatedCustomersArr };
    });
    saveToStorage({ ...get(), customersArr: updatedCustomersArr });
  },
  
  // Helper functions
  getCustomerIdsFromFilters: () => {
    const state = get();
    const orgIdFilter = state.activeFilters.find(
      f => f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true
    );
    return orgIdFilter?.selected || orgIdFilter?.values || [];
  },
  
  hasCustomerSelection: () => {
    const state = get();
    return state.activeFilters.some(
      f => f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true
    );
  },
  
  addCustomerIdsFilter: (ids: string[]) => {
    const state = get();
    // Remove existing orgId filter
    const filtered = state.activeFilters.filter(
      f => !(f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true)
    );
    // Add new orgId filter
    const newFilter: ActiveFilter = {
      dbFieldPath: 'relatedRequests.orgId',
      selected: ids,
      filterObject: 'evidence',
      selectedIsObjId: true,
      name: 'company',
      label: 'Company Name',
      type: 'fetchableCheckbox',
      showInInbox: true,
      parent: '',
      query: null,
      values: ids
    };
    set({ activeFilters: [...filtered, newFilter] });
    saveToStorage({ ...get(), activeFilters: [...filtered, newFilter] });
  },
  
  removeCustomerIdsFilter: () => {
    const state = get();
    const filtered = state.activeFilters.filter(
      f => !(f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true)
    );
    set({ activeFilters: filtered });
    saveToStorage({ ...get(), activeFilters: filtered });
  },
  
  addFilter: (filter: ActiveFilter) => {
    const state = get();
    // Remove existing filter with same dbFieldPath
    const filtered = state.activeFilters.filter(f => f.dbFieldPath !== filter.dbFieldPath);
    set({ activeFilters: [...filtered, filter] });
    saveToStorage({ ...get(), activeFilters: [...filtered, filter] });
  },
  
  removeFilter: (dbFieldPath: string) => {
    const state = get();
    const filtered = state.activeFilters.filter(f => f.dbFieldPath !== dbFieldPath);
    set({ activeFilters: filtered });
    saveToStorage({ ...get(), activeFilters: filtered });
  },

  // Tab-based filter exclusion/restoration (ephemeral)
  excludeFiltersForRoadmap: (dbFieldPathSubstrings) => {
    const state = get();
    if (!Array.isArray(dbFieldPathSubstrings) || dbFieldPathSubstrings.length === 0) return;

    const shouldExclude = (path?: string) =>
      !!path && dbFieldPathSubstrings.some(substr => path.includes(substr));

    const toRemove = state.activeFilters.filter(f => {
      if (f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true) {
        return false;
      }
      return shouldExclude(f.dbFieldPath);
    });
    if (toRemove.length === 0) return;

    const toKeep = state.activeFilters.filter(f => {
      if (f.dbFieldPath === 'relatedRequests.orgId' && f.selectedIsObjId === true) {
        return true;
      }
      return !shouldExclude(f.dbFieldPath);
    });

    // Do NOT persist; this is view-ephemeral
    set({ activeFilters: toKeep, excludedFiltersBackup: toRemove });
  },

  restoreExcludedFiltersForEvidenceTable: () => {
    const state = get();
    if (!state.excludedFiltersBackup || state.excludedFiltersBackup.length === 0) return;

    // Deduplicate by dbFieldPath, preferring current activeFilters
    const existingPaths = new Set(state.activeFilters.map(f => f.dbFieldPath));
    const toReAdd = state.excludedFiltersBackup.filter(f => !existingPaths.has(f.dbFieldPath));
    const merged = [...state.activeFilters, ...toReAdd];

    // Do NOT persist; this is view-ephemeral
    set({ activeFilters: merged, excludedFiltersBackup: [] });
  },
  
  setEvidenceInclusion: (inclusion) => {
    set({ evidenceInclusion: inclusion });
    saveToStorage({ ...get(), evidenceInclusion: inclusion });
  },
  
  setGroupBy: (groupBy) => {
    set({ groupBy });
    saveToStorage({ ...get(), groupBy });
  },
  
  setSort: (sortBy, sortDirection) => {
    set({ sortBy, sortDirection });
    saveToStorage({ ...get(), sortBy, sortDirection });
  },
  
  toggleGroup: (groupValue) =>
    set((state) => {
      const newExpandedGroups = state.expandedGroups.includes(groupValue)
        ? state.expandedGroups.filter((g) => g !== groupValue)
        : [...state.expandedGroups, groupValue];
      return { expandedGroups: newExpandedGroups };
    }),
  
  setExpandedGroups: (groups) => set({ expandedGroups: groups }),
  
  selectIdea: (idea) => set({ selectedIdea: idea, drawerOpen: idea !== null }),

  setSelectedEvidence: (evidence) => set({ selectedEvidence: evidence }),
  
  openDrawer: () => set({ drawerOpen: true }),
  
  closeDrawer: () => set({ drawerOpen: false, selectedIdea: null }),
  
  openModal: () => set({ modalOpen: true }),
  
  closeModal: () => set({ modalOpen: false }),
  
  setDebugMode: (enabled) => set({ debugMode: enabled }),
  
  reset: () => {
    set(initialState);
    localStorage.removeItem('customer360-v2-storage');
  },
  
  // 🔥 NEW: Ideas Management Actions
  setGroupItems: (groupValue, items, totalCount, hasNextPage, nextCursor) => {
    set((state) => ({
      ideasByGroup: {
        ...state.ideasByGroup,
        [groupValue]: {
          items,
          totalCount,
          hasNextPage,
          nextCursor,
        },
      },
    }));
  },
  
  appendGroupItems: (groupValue, items, hasNextPage, nextCursor) => {
    set((state) => {
      const existing = state.ideasByGroup[groupValue];
      if (!existing) {
        // If group doesn't exist, just set it
        return {
          ideasByGroup: {
            ...state.ideasByGroup,
            [groupValue]: {
              items,
              totalCount: items.length,
              hasNextPage,
              nextCursor,
            },
          },
        };
      }
      
      // Append items, avoiding duplicates
      const existingIds = new Set(existing.items.map(i => i._id?.toString()));
      const newItems = items.filter(i => !existingIds.has(i._id?.toString()));
      
      return {
        ideasByGroup: {
          ...state.ideasByGroup,
          [groupValue]: {
            ...existing,
            items: [...existing.items, ...newItems],
            hasNextPage,
            nextCursor,
          },
        },
      };
    });
  },
  
  setGroupLoading: (groupValue, isLoading) => {
    set((state) => {
      const newLoadingGroups = new Set(state.loadingGroups);
      if (isLoading) {
        newLoadingGroups.add(groupValue);
      } else {
        newLoadingGroups.delete(groupValue);
      }
      return { loadingGroups: newLoadingGroups };
    });
  },
  
  getGroupItems: (groupValue) => {
    const state = get();
    return state.ideasByGroup[groupValue]?.items || [];
  },
  
  isGroupLoading: (groupValue) => {
    const state = get();
    return state.loadingGroups.has(groupValue);
  },
  
  clearGroupItems: (groupValue) => {
    set((state) => {
      const newIdeasByGroup = { ...state.ideasByGroup };
      delete newIdeasByGroup[groupValue];
      return { ideasByGroup: newIdeasByGroup };
    });
  },
  
  // 🔥 NEW: Aggregations Management Actions
  setAggregations: (aggregations) => {
    set((state) => {
      const now = Date.now();
      const newCache = { ...state.aggregationsCache };
      
      Object.entries(aggregations).forEach(([ideaId, data]) => {
        newCache[ideaId] = {
          ...data,
          fetchedAt: now,
        };
      });
      
      return { aggregationsCache: newCache };
    });
  },
  
  setAggregationsLoading: (ideaIds, isLoading) => {
    set((state) => {
      const newLoadingAggregations = new Set(state.loadingAggregations);
      
      ideaIds.forEach(id => {
        if (isLoading) {
          newLoadingAggregations.add(id);
        } else {
          newLoadingAggregations.delete(id);
        }
      });
      
      return { loadingAggregations: newLoadingAggregations };
    });
  },
  
  getAggregation: (ideaId) => {
    const state = get();
    const cached = state.aggregationsCache[ideaId];
    if (!cached) return undefined;
    
    // Check if cache is still valid (5 minutes)
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    if (now - cached.fetchedAt > fiveMinutes) {
      return undefined; // Cache expired
    }
    
    return {
      impact: cached.impact,
      customerCount: cached.customerCount,
    };
  },
  
  isAggregationLoading: (ideaId) => {
    const state = get();
    return state.loadingAggregations.has(ideaId);
  },
  
  clearAggregationsCache: () => {
    set({ aggregationsCache: {}, loadingAggregations: new Set() });
  },
  
  // 🔥 NEW: Group Aggregates Management Actions
  setGroupAggregate: (groupValue, data) => {
    set((state) => ({
      groupAggregatesCache: {
        ...state.groupAggregatesCache,
        [groupValue]: { ...data, fetchedAt: Date.now() }
      }
    }));
  },
  
  setGroupAggregates: (aggregates) => {
    set((state) => {
      const now = Date.now();
      const newCache = { ...state.groupAggregatesCache };
      Object.entries(aggregates).forEach(([groupValue, data]) => {
        newCache[groupValue] = { ...data, fetchedAt: now };
      });
      return { groupAggregatesCache: newCache };
    });
  },
  
  setGroupAggregatesLoading: (groupValues, isLoading) => {
    set((state) => {
      const newSet = new Set(state.loadingGroupAggregates);
      groupValues.forEach(gv => {
        isLoading ? newSet.add(gv) : newSet.delete(gv);
      });
      return { loadingGroupAggregates: newSet };
    });
  },
  
  getGroupAggregate: (groupValue) => {
    const state = get();
    const cached = state.groupAggregatesCache[groupValue];
    if (!cached) return undefined;
    
    // Check cache validity (5 minutes)
    const fiveMinutes = 5 * 60 * 1000;
    if (Date.now() - cached.fetchedAt > fiveMinutes) {
      return undefined;
    }
    
    return cached;
  },
  
  isGroupAggregateLoading: (groupValue) => {
    return get().loadingGroupAggregates.has(groupValue);
  },
  
  clearGroupAggregatesCache: () => {
    set({ groupAggregatesCache: {}, loadingGroupAggregates: new Set() });
  },
}));

